/**
 * Utility functions for sanitizing form data
 */

/**
 * Sanitize a single value for FormData
 * @param {*} value - The value to sanitize
 * @returns {string|File} - Sanitized value
 */
export const sanitizeValue = (value) => {
	if (value === null || value === undefined) {
		return '';
	}
	
	if (value instanceof Date) {
		return value.toISOString();
	}
	
	if (value instanceof File) {
		return value;
	}
	
	if (typeof value === 'object') {
		return JSON.stringify(value);
	}
	
	return value;
};

/**
 * Append an object's properties to FormData with a prefix
 * @param {FormData} formData - The FormData object to append to
 * @param {Object} data - The data object to process
 * @param {string} prefix - Prefix for field names
 * @param {Array<string>} skipKeys - Keys to skip
 */
export const appendObjectToFormData = (formData, data, prefix = '', skipKeys = []) => {
	if (!data || typeof data !== 'object') {
		return;
	}
	
	Object.entries(data).forEach(([key, value]) => {
		if (skipKeys.includes(key)) {
			return;
		}
		
		const fieldName = prefix ? `${prefix}_${key}` : key;
		const sanitizedValue = sanitizeValue(value);
		
		if (!(sanitizedValue instanceof File)) {
			formData.append(fieldName, sanitizedValue);
		}
	});
};

/**
 * Append address object to FormData
 * @param {FormData} formData - The FormData object
 * @param {Object} address - Address object
 * @param {string} addressType - Type of address (mailing/permanent)
 */
export const appendAddressToFormData = (formData, address, addressType) => {
	if (!address || typeof address !== 'object') {
		return;
	}
	
	const fields = ['address', 'city', 'state', 'country', 'zipCode'];
	fields.forEach(field => {
		formData.append(`${addressType}Address_${field}`, address[field] || '');
	});
};

/**
 * Append file to FormData if it's a File object, or preserve URL if it's a string
 * @param {FormData} formData - The FormData object
 * @param {File|string|null} file - File object or URL string
 * @param {string} fileFieldName - Field name for file
 * @param {string} urlFieldName - Field name for URL
 */
export const appendFileOrUrl = (formData, file, fileFieldName, urlFieldName) => {
	if (!file) {
		return;
	}
	
	if (file instanceof File) {
		formData.append(fileFieldName, file);
	} else if (typeof file === 'string') {
		formData.append(urlFieldName, file);
	}
};
